import { useEffect, useState } from 'react'
import Card from '@/components/ui/Card'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'

interface DonationRow {
  program_id: string;
  amount_cents: number;
  created_at: string;
}

export default function MyGiving() {
  const { session } = useAuth()
  const [rows, setRows] = useState<DonationRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    const loadDonations = async () => {
      if (!session?.access_token) {
        if (active) setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('donations')
          .select('program_id, amount_cents, created_at')
          .order('created_at', { ascending: false })

        if (error) throw error
        if (active) setRows((data as unknown as DonationRow[]) || [])
      } catch (error) {
        console.error('Failed to load donations', error)
        if (active) setRows([])
      } finally {
        if (active) setLoading(false)
      }
    }

    loadDonations()
    return () => {
      active = false
    }
  }, [session?.access_token])

  if (loading) {
    return <div className="p-6 text-cocoa/70">Loading…</div>
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-cocoa">My Giving</h1>
        <p className="text-sm text-cocoa/70">A history of your donations across programs.</p>
      </header>
      <Card className="overflow-hidden p-0">
        <table className="min-w-full text-sm">
          <thead className="bg-shell text-cocoa/70">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Program</th>
              <th className="px-4 py-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.created_at}-${index}`} className="border-t border-sand/60">
                <td className="px-4 py-3">{new Date(row.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{row.program_id}</td>
                <td className="px-4 py-3 text-right">${(row.amount_cents / 100).toFixed(2)}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-cocoa/60" colSpan={3}>
                  No donations yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
